import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface SidebarApi {
  /** Whether the drawer is open on small screens. */
  open: boolean;
  setOpen: (v: boolean) => void;
  /** True only while a page that actually has a sidebar is mounted. */
  hasSidebar: boolean;
  setHasSidebar: (v: boolean) => void;
}

const SidebarCtx = createContext<SidebarApi | null>(null);

/**
 * The drawer toggle lives in the top nav (Layout) while the drawer itself is
 * owned by the page that has the folder data, so the open state is shared here
 * rather than duplicated.
 */
export function SidebarProvider({ children }: { children: ReactNode }) {
  const [open, setOpenState] = useState(false);
  const [hasSidebar, setHasSidebarState] = useState(false);

  const setOpen = useCallback((v: boolean) => setOpenState(v), []);
  const setHasSidebar = useCallback((v: boolean) => {
    setHasSidebarState(v);
    if (!v) setOpenState(false);
  }, []);

  const value = useMemo(
    () => ({ open, setOpen, hasSidebar, setHasSidebar }),
    [open, setOpen, hasSidebar, setHasSidebar],
  );
  return <SidebarCtx.Provider value={value}>{children}</SidebarCtx.Provider>;
}

export function useSidebar(): SidebarApi {
  const ctx = useContext(SidebarCtx);
  if (!ctx) throw new Error("useSidebar must be used within SidebarProvider");
  return ctx;
}
